import type { Storage as Interface } from "../../design/services";
import type { Config } from "../../design/types";

export class Storage implements Interface {
    private files: Map<string, File> = new Map();
    private stagingFiles: Map<string, File> = new Map();

    constructor(_config: Config) { }

    ["constructor"](config: Config): Interface {
        return new Storage(config);
    }

    async upload(content: File, staging?: boolean): Promise<string> {
        // Validate input
        if (!content || !(content instanceof File)) {
            throw new Error("Invalid file: upload requires a File object");
        }

        // Create blob URL that browsers can load directly
        const url = URL.createObjectURL(content);

        // Store file in appropriate store
        if (staging) {
            this.stagingFiles.set(url, content);
        } else {
            this.files.set(url, content);
        }

        return url;
    }

    async download(url: string): Promise<File> {
        if (typeof url !== 'string' || url.trim() === '') {
            throw new Error("Invalid URL: download requires a valid URL string");
        }

        const file = this.stagingFiles.get(url) ?? this.files.get(url);

        if (!file) {
            throw new Error(`File not found at URL: ${url}`);
        }

        return file;
    }

    async commit(urls: string[]): Promise<Record<string, string>> {
        // Phase 1: Collect files to commit (staging untouched — safe to retry on failure)
        const results: { url: string; file: File }[] = [];
        for (const url of urls) {
            const file = this.stagingFiles.get(url);
            if (!file) continue;
            results.push({ url, file });
        }

        // Phase 2: Promote to real store and clean up staging
        const mapping: Record<string, string> = {};
        for (const { url, file } of results) {
            this.files.set(url, file);
            this.stagingFiles.delete(url);
            mapping[url] = url;
        }
        return mapping;
    }

    async discard(urls: string[]): Promise<void> {
        for (const url of urls) {
            this.stagingFiles.delete(url);
        }
    }
}
